import clsx from 'clsx';
import React from 'react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import Button from '../Button';
import { useApiClients } from '../../client/api/useApiClient';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { toggleModal } from '../../reducers/modalToggle';
import { toast } from 'react-toastify';
import { queryClient } from '../../main';

interface CancelSubscriptionModalProps {
  otherClasses?: string;
  handleClose: () => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  otherClasses,
  handleClose,
}) => {
  const cancelSubscriptionModalClasses = clsx(otherClasses);
  const dispatch = useDispatch();

  const lg = useButtonSizeLarge();

  const { StripeApi } = useApiClients();

  const cancelSubscription = useMutation<any, Error, any>({
    mutationFn: async () => {
      const response = await StripeApi?.stripeControllerCancelSubscription();
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['get-subscription-details'], (oldData: any) => {
        return {
          ...oldData,
          currentPlan: {
            ...oldData?.currentPlan,
            subscription: {
              ...data?.subscription,
            },
          },
        };
      });
      toast.success(data?.message);
      dispatch(toggleModal(''));
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || error?.response?.data?.error?.message;

      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  return (
    <div
      className={cancelSubscriptionModalClasses}
      data-testid="cancel-subscription-modal"
    >
      <div className="mb-8 flex w-full flex-col items-center gap-2.5 text-center lg:mb-12">
        <WarningIcon className="[&>path]:fill-brand-posh-pink h-12 w-12 md:h-85 md:w-85" />
        <h4 className="text-brand-posh-pink">
          Are you sure you want to cancel?{' '}
        </h4>
        <p className="text-brand-posh-pink font-nonrmal text-lg leading-5">
          Clicking Cancel will end your subscription with EHS. Please note, this
          action is irreversible.
        </p>
      </div>
      <div className="mt-8 flex flex-col-reverse gap-2.5 sm:grid sm:grid-cols-2 sm:gap-6 lg:mt-12">
        <Button
          otherClasses="!text-brand-posh-pink hover:text-brand-posh-pink hover:border-brand-posh-pink "
          size={lg}
          variant="secondary"
          onClick={() => handleClose()}
          disable={cancelSubscription?.isPending}
        >
          Close
        </Button>
        <Button
          otherClasses="bg-brand-posh-pink hover:text-brand-posh-pink hover:bg-transparent hover:border-brand-posh-pink"
          size={lg}
          disable={cancelSubscription?.isPending}
          loading={cancelSubscription.isPending}
          onClick={() => cancelSubscription?.mutate({})}
        >
          Cancel Subscription
        </Button>
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;
